import axios from 'axios';
import { apiCache } from '../utils/apiCache';
import { requestQueue } from '../utils/requestQueue';

const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/v1` 
  : 'http://localhost:5001/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000, // Increased timeout to 20 seconds
  headers: {
    'Content-Type': 'application/json'
  }
});

// Enhanced error interceptor with better 429 handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 429) {
      const message = 'Rate limit exceeded. Please wait a moment and try again.';
      return Promise.reject(new Error(message));
    }
    const message = error.response?.data?.message || error.message || 'Something went wrong';
    return Promise.reject(new Error(message));
  }
);

// Enhanced wrapper for API calls with caching, rate limiting, and retry logic
const cachedRequest = async (url, config = {}) => {
  const cacheKey = apiCache.generateKey(url, config.params);

  // Check cache first - if data exists, return immediately
  const cachedData = apiCache.get(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // Add to request queue with retry logic to prevent rate limiting
  const data = await requestQueue.add(async () => {
    try {
      return await api.get(url, config);
    } catch (error) {
      // If 429, let the queue handle retries
      if (error.message && error.message.includes('429')) {
        throw error;
      }
      // For other errors, throw immediately
      throw error;
    }
  });

  // Cache the successful result
  apiCache.set(cacheKey, data);

  return data;
};

export const apiService = {
  healthCheck: () => cachedRequest('/health'),
  getTeams: (params = {}) => cachedRequest('/teams', { params }),
  getTeamById: (id) => cachedRequest('/teams/' + id),
  getPlayers: (params = {}) => cachedRequest('/players', { params }),
  getMatches: (params = {}) => cachedRequest('/matches', { params }),
  getMatchById: (id) => cachedRequest('/matches/' + id),
  getTeamStatistics: () => cachedRequest('/statistics/teams'),
  getPlayerStatistics: (params = {}) => cachedRequest('/statistics/players', { params }),
  getSeasonStatistics: () => cachedRequest('/statistics/seasons'),

  // Dataset endpoints with caching and rate limiting
  getBattingStats: (statType) => cachedRequest(`/dataset/batting/${statType}`),
  getBowlingStats: (statType) => cachedRequest(`/dataset/bowling/${statType}`),
  getTeamStats: (statType) => cachedRequest(`/dataset/team-stats/${statType}`),
  getDatasetTeams: () => cachedRequest('/dataset/teams'),
  getDatasetMatches: () => cachedRequest('/dataset/matches'),
  getStandings: () => cachedRequest('/dataset/standings'),
  getSquads: () => cachedRequest('/dataset/squads'),
  getAllMatchFiles: () => cachedRequest('/dataset/matches/files'),
  getMatchInfo: (matchId) => cachedRequest(`/dataset/matches/info/${matchId}`),
  getMatchByFilename: (filename) => cachedRequest(`/dataset/matches/${filename}`),
  getScorecard: (filename) => cachedRequest(`/dataset/matches/${filename}/scorecard`),
  getMatchCommentary: (filename, innings) => cachedRequest(`/dataset/matches/${filename}/commentary/${innings}`),
  getWagonWheel: (filename) => cachedRequest(`/dataset/matches/${filename}/wagon-wheel`),
  getLiveDetails: (filename) => cachedRequest(`/dataset/matches/${filename}/live`),
  getAllPlayerFiles: () => cachedRequest('/dataset/players/files'),
  getPlayerCareerStats: (playerId) => cachedRequest(`/dataset/players/${playerId}/stats`)
};

export default api;
